/** BarLineChart — bars (left axis) with an overlaid line (right axis), on Recharts.
 *
 *  Both axes are LABELLED: the line used to be drawn against an invisible scale, so its
 *  shape was readable but its values were not. Two series, so a legend is always present
 *  — identity is never colour alone.
 *
 *  Recharts owns the sizing (ResponsiveContainer is a ResizeObserver), the ticks, the
 *  crosshair and the tooltip. The bar takes a share of its band rather than a fixed pixel
 *  width, so it grows with the panel; `radius` rounds the data-end and leaves the baseline
 *  square. */
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { useTokens } from './useTokens'
import { DOT_R, LINE_W } from './chartMarks'
import { axisStyle, gridStroke, tooltipStyle } from './rechartsTheme'

interface Bar_ {
  v: number
  label: string
  color?: string
}
interface Props {
  bars: Bar_[]
  line: number[]
  lineColor?: string
  barName?: string
  lineName?: string
  fmtBar?: (v: number) => string
  fmtLine?: (v: number) => string
}

const H = 250

const compact = (v: number): string =>
  Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}m` : Math.abs(v) >= 1e3 ? `${Math.round(v / 1e3)}k` : `${Math.round(v)}`

export default function BarLineChart({
  bars, line, lineColor, barName = 'Value', lineName = 'Rate', fmtBar = compact, fmtLine = compact,
}: Props) {
  const t = useTokens()
  const lc = lineColor || t.purple
  const bc = bars[0]?.color || t.blue
  const data = bars.map((b, i) => ({ label: b.label, v: b.v, rate: line[i] ?? null }))
  return (
    <ResponsiveContainer width="100%" height={H}>
      {/* barCategoryGap is a PERCENTAGE, so the bar keeps its share of the band as the
          panel grows. A fixed pixel cap does the opposite: the wider the chart, the more
          of it is gap. It applies to BOTH sides, so 22% leaves the bar 56% of its band. */}
      <ComposedChart data={data} margin={{ top: 12, right: 8, bottom: 4, left: 0 }} barCategoryGap="22%">
        <CartesianGrid vertical={false} stroke={gridStroke} />
        <XAxis dataKey="label" {...axisStyle} interval="preserveStartEnd" minTickGap={16} />
        <YAxis yAxisId="left" tickFormatter={fmtBar} width={62} {...axisStyle} axisLine={false} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={fmtLine} width={54} {...axisStyle} axisLine={false} />
        <Tooltip
          cursor={{ fill: 'var(--panel2)', fillOpacity: 0.6 }}
          formatter={(v: number, name: string) => [name === lineName ? fmtLine(v) : fmtBar(v), name]}
          contentStyle={tooltipStyle}
        />
        <Legend wrapperStyle={{ fontSize: 11, color: 'var(--mut)' }} iconSize={9} />
        <Bar yAxisId="left" dataKey="v" name={barName} fill={bc} fillOpacity={0.92}
          radius={[4, 4, 0, 0]} isAnimationActive={false} />
        <Line yAxisId="right" dataKey="rate" name={lineName} stroke={lc} strokeWidth={LINE_W}
          strokeLinecap="round" strokeLinejoin="round" connectNulls={false} isAnimationActive={false}
          dot={{ r: DOT_R, fill: lc, stroke: 'var(--panel)', strokeWidth: 2 }} activeDot={{ r: DOT_R + 1 }} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
